import { Directive, input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { get$ as getFunction$ } from '@vendasta/protos/ai_assistants/v1alpha1/function-service';
import {
  ConfigurableGoal,
  Goal,
  GoalKey,
  GoalType,
  KeyValuePair,
  Namespace,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { firstValueFrom, map, Observable, shareReplay } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';
import { DEFAULT_LEAD_CAPTURE_GOAL_ID } from '../ai-assistant.constants';
import { AiToolFormRegistryService } from '../services/ai-tool-form-registry.service';
import { extractVariablesFromMultipleSources } from '../utils/variable-detection.util';
import { extractTextForVariableDetection } from '../utils/variable-extraction.util';
import { AiConstraintsForm } from './ai-constraints.form';
import { AiPromptControl } from './ai-prompt.form';
import { AiToolForm, AiToolFormArray } from './ai-tools.form';
import { FormArrayWithDirtyTracking } from './forms';

export type CapabilityConfigurationFormControl = FormControl<KeyValuePair[] | null>;

export function getKeyValuePairFromCapabilityFormControl(
  formControl: CapabilityConfigurationFormControl,
  key: string,
): KeyValuePair {
  const formValue = formControl.value || [];
  return formValue?.find((pair) => pair.key === key) || { key, value: '' };
}

export function setKeyValuePairFromCapabilityFormControl(
  formControl: CapabilityConfigurationFormControl,
  newPair: KeyValuePair,
): void {
  const formValue = formControl.value ?? [];
  const idx = formValue.findIndex((pair) => pair.key === newPair.key);
  const next = idx >= 0 ? formValue.map((p, i) => (i === idx ? newPair : p)) : [...formValue, newPair];
  formControl.setValue(next);
}

// Capabilities
export type AiCapabilityFormControls = {
  goalId: FormControl<string | null>;
  // TODO: Can this be non-nullable?
  namespace: FormControl<Namespace | null>;
  managed: FormControl<boolean>;
  name: FormControl<string | null>;
  description: FormControl<string | null>;
  type: FormControl<GoalType | null>;
  prompt: AiPromptControl;
  tools: AiToolFormArray;
  configuration: CapabilityConfigurationFormControl;
  constraints: AiConstraintsForm;
  overrideOf: FormControl<GoalKey | null>;
};

export class AiCapabilityForm extends FormGroup<AiCapabilityFormControls> {
  isHidden = false;

  // Observable of available variables (variables found in text)
  readonly availableVariables$: Observable<string[]>;

  /**
   * The prompt modules the loaded goal referenced, as keys.
   *
   * `UpsertAssistant` applies no field mask, so a payload omitting the list clears it. The tree normally puts the
   * stored list back by merging over the loaded goal, but a capability the picker added is not on the loaded
   * assistant for it to merge from.
   */
  private readonly loadedPromptModules: Goal['promptModules'] | undefined;

  constructor(goal?: Partial<ConfigurableGoal>, toolFormRegistry?: AiToolFormRegistryService) {
    const capability = goal?.goal ?? null;
    const keyValuePairs = goal?.configuration?.filter((v) => Boolean(v.key && v.value));

    super({
      goalId: new FormControl({ value: capability?.id ?? null, disabled: true }),
      namespace: new FormControl({ value: capability?.namespace ?? null, disabled: true }),
      managed: new FormControl({ value: capability?.managed ?? false, disabled: true }, { nonNullable: true }),
      name: new FormControl(capability?.name ?? null, Validators.required),
      description: new FormControl(capability?.description ?? null),
      type: new FormControl({ value: capability?.type ?? GoalType.GOAL_TYPE_CUSTOM, disabled: true }),
      prompt: new AiPromptControl(capability?.prompt),
      tools: new AiToolFormArray(capability?.functions ?? [], toolFormRegistry),
      configuration: new FormControl<KeyValuePair[] | null>(keyValuePairs ?? null),
      constraints: new AiConstraintsForm(capability?.constraints ?? []),
      overrideOf: new FormControl<GoalKey | null>(capability?.overrideOf ?? null),
    });

    this.loadedPromptModules = capability?.promptModules;

    this.controls.configuration.enable();
    this.updateValueAndValidity({ emitEvent: false });

    const isPersonalityType = this.controls.type.getRawValue() === GoalType.GOAL_TYPE_PERSONALITY;
    const hasViewDenyConstraint = this.controls.constraints.controls.isHidden.getRawValue();
    this.isHidden = isPersonalityType || hasViewDenyConstraint;

    // Build text sources observable
    const textSources$ = this.valueChanges.pipe(
      debounceTime(800),
      startWith(null),
      map(() => extractTextForVariableDetection(this.controls.prompt.getRawValue(), this.controls.tools.controls)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    // Build available variables observable from text sources
    this.availableVariables$ = textSources$.pipe(
      map((textSources) => extractVariablesFromMultipleSources(textSources)),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  resolveGoalId(): string {
    return this.controls.overrideOf?.getRawValue()?.id || this.controls.goalId.getRawValue() || '';
  }

  /**
   * Static factory method to create AiCapabilityForm with full function details
   */
  static async createWithFunctionDetails(
    goal?: Partial<ConfigurableGoal>,
    toolFormRegistry?: AiToolFormRegistryService,
    fetchFunctions = true,
  ): Promise<AiCapabilityForm> {
    let enrichedGoal = goal;

    // Fetch function details if requested and goal has functions
    if (fetchFunctions && goal?.goal?.functions?.length && toolFormRegistry) {
      const enrichedGoalData = await AiCapabilityForm.fetchFunctionDetailsForGoal(goal.goal, toolFormRegistry);
      // Casts: proto's Partial<> only wraps level-1 request fields; deeper nested fields stay non-Partial.
      enrichedGoal = {
        ...goal,
        goal: enrichedGoalData as ConfigurableGoal['goal'],
      };
    }

    return new AiCapabilityForm(enrichedGoal, toolFormRegistry);
  }

  /**
   * Fetches full function details for all functions in a goal, using tool registry when available
   */
  private static async fetchFunctionDetailsForGoal(
    goal: Partial<Goal>,
    toolFormRegistry: AiToolFormRegistryService,
  ): Promise<Partial<Goal>> {
    if (!goal.functions?.length) {
      return goal;
    }

    // Get full function details for each function in the goal
    const functionPromises = goal.functions.map(async (functionKey) => {
      if (!functionKey.id || !functionKey.namespace) {
        return null;
      }

      // Check if we already have this function in the tool registry
      if (toolFormRegistry.hasRegisteredForm(functionKey.id)) {
        const existingForm = toolFormRegistry.getOrRegister(functionKey.id, () => new AiToolForm());
        return existingForm.toFunction(functionKey.namespace);
      }

      // Function not in registry, fetch it from proto API
      const response = await firstValueFrom(getFunction$({ id: functionKey.id, namespace: functionKey.namespace }));
      const fetchedFunction = response?.function;
      if (!fetchedFunction) return null;
      toolFormRegistry.getOrRegister(functionKey.id, () => new AiToolForm({ func: fetchedFunction }));
      return fetchedFunction;
    });

    const functionDetails = await Promise.all(functionPromises);
    const validFunctions = functionDetails.filter((fn): fn is Partial<Goal['functions'][number]> => fn != null);

    // Casts: proto's Partial<> only wraps level-1 request fields; deeper nested fields stay non-Partial.
    return {
      ...goal,
      functions: validFunctions as Goal['functions'],
    };
  }

  toGoalKey(): Partial<GoalKey> | null {
    const id = this.controls.goalId.getRawValue();
    const namespace = this.controls.namespace.getRawValue();
    if (!id || !namespace) return null;
    return { id, namespace };
  }

  /** What identifies this capability's goal, for a save that has to put it back after a failed call. */
  snapshotIdentity(): AiCapabilityIdentity {
    return {
      goalId: this.controls.goalId.getRawValue(),
      namespace: this.controls.namespace.getRawValue(),
      managed: this.controls.managed.getRawValue(),
      overrideOf: this.controls.overrideOf.getRawValue(),
    };
  }

  restoreIdentity(identity: AiCapabilityIdentity): void {
    this.controls.goalId.setValue(identity.goalId);
    this.controls.namespace.setValue(identity.namespace);
    this.controls.managed.setValue(identity.managed);
    this.controls.overrideOf.setValue(identity.overrideOf);
  }

  /** Stands the goal up under the ID a save is sending it as, or the one the save assigned it. */
  assignGoalId(id: string | null): void {
    this.controls.goalId.setValue(id);
  }

  // TODO: We need to ensure this is called after...
  //  toGoal is called + the new goal is created + the goalID is set on the form
  // in the modified global/managed case
  toConfigurableGoal(fallbackNamespace: Namespace | undefined): Partial<ConfigurableGoal> {
    // Casts: proto's Partial<> only wraps level-1 request fields; deeper nested fields stay non-Partial.
    return {
      goal: {
        id: this.controls.goalId.getRawValue() ?? undefined,
        namespace: this.controls.namespace.getRawValue() ?? fallbackNamespace,
      } as ConfigurableGoal['goal'],
      configuration: this.controls.configuration.getRawValue() ?? undefined,
    };
  }

  // toGoal creates a goal that can be used in an create/update/upsert goal API call
  // - If the form contains a global goal, Then a new locally namespaced goal
  //   will be returned with a reference to the goal it is based on.
  // - If the form contains a managed goal, Then a new locally namespaced goal
  //   that is not managed will be returned with a reference to the goal it is based on.
  // In both of the cases above, the form values will be modified
  toGoal(localNamespace: Namespace): Partial<Goal> {
    if (this.needsGoalForking()) {
      const originalGoalId = this.controls.goalId.getRawValue();
      const originalGoalNamespace = this.controls.namespace.getRawValue();
      this.controls.overrideOf.setValue({
        id: originalGoalId ?? '',
        namespace: originalGoalNamespace ?? undefined,
      });
      this.controls.goalId.setValue(null);
      this.controls.namespace.setValue(localNamespace);
      this.controls.managed.setValue(false);
    }

    // Casts: proto's Partial<> only wraps level-1 request fields; deeper nested fields stay non-Partial.
    return {
      id: this.controls.goalId.getRawValue() ?? undefined,
      name: this.controls.name.getRawValue() ?? undefined,
      namespace: this.controls.namespace.getRawValue() ?? localNamespace,
      description: this.controls.description.getRawValue() ?? undefined,
      type: this.controls.type.getRawValue() ?? undefined,
      functions: this.controls.tools.toFunctionKeys(localNamespace) as Goal['functions'],
      ...this.promptPayload(),
      constraints: this.controls.constraints.toConstraints() as Goal['constraints'],
      overrideOf: this.controls.overrideOf.getRawValue() ?? undefined,
    };
  }

  /**
   * What the payload says about the prompt.
   *
   * The backend mirrors a stated prompt into the goal's prompt module, so the module list is its to manage and
   * this states it only to clear it: on the unmasked tree an empty list is the only thing that reads as a clear,
   * since a goal carrying no prompt is taken to be echoing its stored one. Unit 13 of the collapse plan removes
   * the field and the mirror with it, and moves the clear onto the masked path.
   */
  private promptPayload(): Pick<Partial<Goal>, 'prompt' | 'promptModules'> {
    // A locked prompt is not this save's to state.
    if (this.controls.prompt.disabled) {
      return {};
    }
    const prompt = this.controls.prompt.getRawValue();
    if (prompt) {
      return { prompt };
    }
    return this.controls.prompt.dirty ? { prompt: '', promptModules: [] } : {};
  }

  /** Identity only: a module carrying a name reads to the backend as a description rather than a reference. */
  loadedPromptModuleKeys(): Goal['promptModules'] {
    return (this.loadedPromptModules ?? []).map(({ id, namespace }) => ({ id, namespace })) as Goal['promptModules'];
  }

  isOverride(): boolean {
    return Boolean(this.controls.overrideOf.getRawValue());
  }

  isGlobalOrManaged(): boolean {
    const ns = this.controls.namespace.getRawValue();
    return Boolean(ns?.globalNamespace) || this.controls.managed.getRawValue();
  }

  isLeadCaptureCapability(): boolean {
    return this.controls.goalId.getRawValue() === DEFAULT_LEAD_CAPTURE_GOAL_ID;
  }

  hasOnlyConfigurationChanges(): boolean {
    // If configuration is dirty but the goal definition fields are not dirty
    const configurationDirty = this.controls.configuration.dirty;
    const goalDefinitionDirty = Object.entries(this.controls)
      .filter(([key]) => key !== 'configuration')
      .some(([, control]) => control.dirty);

    return configurationDirty && !goalDefinitionDirty;
  }

  needsGoalForking(): boolean {
    return this.isGlobalOrManaged() && !this.hasOnlyConfigurationChanges();
  }

  patchCapability(form: AiCapabilityForm): void {
    const suppressEvents = { emitEvent: false };
    const replacementValues = form.getRawValue();
    this.patchValue(replacementValues, { emitEvent: false });

    this.controls.tools.clear(suppressEvents);
    form.controls.tools.controls.forEach((toolForm) => {
      this.controls.tools.push(toolForm, suppressEvents);
    });

    this.controls.constraints.controls.assignmentConstraints.clear(suppressEvents);
    form.controls.constraints.controls.assignmentConstraints.controls.forEach((constraintForm) => {
      this.controls.constraints.controls.assignmentConstraints.push(constraintForm, suppressEvents);
    });

    // Manually handle the constraints invokeConstraints FormArray
    this.controls.constraints.controls.invokeConstraints.clear();
    form.controls.constraints.controls.invokeConstraints.controls.forEach((constraintForm) => {
      this.controls.constraints.controls.invokeConstraints.push(constraintForm);
    });
  }
}

/** The identity controls a save's fork and placeholder mutations overwrite. */
export interface AiCapabilityIdentity {
  goalId: string | null;
  namespace: Namespace | null;
  managed: boolean;
  overrideOf: GoalKey | null;
}

export class AiCapabilityFormArray extends FormArrayWithDirtyTracking<AiCapabilityForm> {
  constructor(capabilities: Partial<ConfigurableGoal>[], toolFormRegistry?: AiToolFormRegistryService) {
    super(capabilities.map((capability) => new AiCapabilityForm(capability, toolFormRegistry)));
  }

  /**
   * Static factory method to create AiCapabilityFormArray with full function details
   */
  static async createWithFunctionDetails(
    capabilities: Partial<ConfigurableGoal>[],
    toolFormRegistry?: AiToolFormRegistryService,
    fetchFunctions = true,
  ): Promise<AiCapabilityFormArray> {
    // Create all capability forms with function details concurrently
    const capabilityFormPromises = capabilities.map((capability) =>
      AiCapabilityForm.createWithFunctionDetails(capability, toolFormRegistry, fetchFunctions),
    );

    const capabilityForms = await Promise.all(capabilityFormPromises);

    // Create the form array with the enriched forms
    const formArray = new AiCapabilityFormArray([], toolFormRegistry);

    // Clear the initial empty array and add the enriched forms
    formArray.clear();
    capabilityForms.forEach((form) => formArray.push(form));

    return formArray;
  }

  toConfigurableGoals(localNamespace: Namespace): Partial<ConfigurableGoal>[] {
    return this.controls
      .filter((goal) => {
        // This prevents us from calling upsertAssistant with the empty personality goal we add when no personality goal exists on the assistant
        return !(
          goal.controls.type.getRawValue() === GoalType.GOAL_TYPE_PERSONALITY && !goal.controls.goalId.getRawValue()
        );
      })
      .map((capabilityForm) => capabilityForm.toConfigurableGoal(localNamespace));
  }
}

@Directive()
export abstract class CustomCapabilityConfigurationForm {
  configurationForm = input.required<CapabilityConfigurationFormControl>();
  goalId = input<string>();
  disableConfigurableGoalFields = input<boolean>(false);
}
